import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NAV_GROUPS, filterGroupsByPermissions, normalize, DefaultNavIcon } from "@/lib/navConfig";
import { Search, ArrowUp, ArrowDown, CornerDownLeft, Command } from "lucide-react";

/**
 * Command Palette global (⌘K / Ctrl+K).
 *
 * - Se abre desde cualquier lugar de la app con la combinación de teclas.
 * - Búsqueda difusa por label + keywords + grupo + ruta.
 * - Navegación con teclado: ↑ ↓ para moverse, ↵ para ir, Esc para cerrar.
 * - Respeta permisos (module/submodule/role) del usuario actual.
 */
export default function CommandPalette({ open, onClose }) {
  const { user, hasModule, hasSubmodule } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      // Delay focus para asegurar que el modal está renderizado
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const visibleGroups = useMemo(
    () => filterGroupsByPermissions(NAV_GROUPS, { role: user?.role, hasModule, hasSubmodule }),
    [user?.role, hasModule, hasSubmodule]
  );

  // Aplanar todos los items visibles con referencia al grupo
  const allItems = useMemo(() => {
    const out = [];
    for (const g of visibleGroups) {
      for (const it of g.items) {
        out.push({ ...it, _grupo: g.label, _grupoKey: g.key });
      }
    }
    return out;
  }, [visibleGroups]);

  const results = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return allItems; // sin query: mostrar todos
    return allItems
      .map((it) => {
        const hay = normalize(`${it.label} ${it._grupo} ${it.to} ${it.keywords || ""}`);
        // Puntuación simple: label prefix > label contains > keywords/route contains
        const label = normalize(it.label);
        let score = 0;
        if (label.startsWith(q)) score += 100;
        else if (label.includes(q)) score += 50;
        if (hay.includes(q)) score += 10;
        return { it, score };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.it);
  }, [query, allItems]);

  // Reset índice cuando cambian los resultados
  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const go = (it) => {
    onClose?.();
    if (it?.to) navigate(it.to);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIdx]) go(results[activeIdx]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose?.();
    }
  };

  // Auto-scroll al item activo
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-xl bg-white rounded-lg shadow-2xl border border-[#d1dbd6] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 border-b border-[#eef1ef]">
          <Search size={16} className="text-[#8ba397] shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar módulo, reporte o acción…"
            className="flex-1 py-3 text-sm text-[#15241d] placeholder:text-[#8ba397] outline-none bg-transparent"
            data-testid="command-palette-input"
          />
          <span className="text-[10px] uppercase tracking-wider text-[#8ba397] font-semibold px-2 py-0.5 border border-[#d1dbd6] rounded">
            Esc
          </span>
        </div>
        <div
          ref={listRef}
          className="max-h-[52vh] overflow-y-auto py-1"
          data-testid="command-palette-results"
        >
          {results.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-[#8ba397]">
              Sin resultados para <span className="font-mono text-[#5c7368]">&quot;{query}&quot;</span>
            </div>
          )}
          {results.map((it, idx) => {
            const Icon = it.icon || DefaultNavIcon;
            const active = idx === activeIdx;
            return (
              <button
                key={it.to}
                type="button"
                data-idx={idx}
                data-testid={`command-palette-item-${it.to.replace(/\//g, "_")}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => go(it)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-l-2 ${
                  active
                    ? "bg-[#e9f2ee] border-[#205c45]"
                    : "border-transparent hover:bg-[#f7f9f8]"
                }`}
              >
                <Icon size={16} className={active ? "text-[#205c45]" : "text-[#5c7368]"} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm truncate ${active ? "text-[#15241d] font-medium" : "text-[#15241d]"}`}>
                    {it.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#8ba397] truncate">
                    {it._grupo}
                  </div>
                </div>
                {active && (
                  <CornerDownLeft size={12} className="text-[#5c7368]" />
                )}
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t border-[#eef1ef] bg-[#f7f9f8] flex items-center gap-3 text-[10px] text-[#5c7368]">
          <span className="flex items-center gap-1"><ArrowUp size={11} /><ArrowDown size={11} /> navegar</span>
          <span className="flex items-center gap-1"><CornerDownLeft size={11} /> abrir</span>
          <span className="ml-auto flex items-center gap-1">
            <Command size={11} /> K para abrir en cualquier momento
          </span>
        </div>
      </div>
    </div>
  );
}
